import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import HeroAnimation from "@/components/HeroAnimation";
import GoogleReviews from "@/components/GoogleReviews";
import Seo from "@/components/Seo";
import { Sun, TreePine, Sprout, Flower2, HandHeart, MapPin, Handshake, ClipboardList, Palette, Truck, ArrowRight, CheckCircle2, TrendingUp, Plus, Minus } from "lucide-react";
import { api, absUrl } from "@/lib/api";

// -- Welcome ----------------------------------------------------------------
function Welcome() {
  const bullets = [
    { icon: Sun, text: "Beautify your outdoor living environment" },
    { icon: TrendingUp, text: "Build valuable equity in your home" },
    { icon: Sprout, text: "Create new spaces for fun outside activities" },
    { icon: Flower2, text: "Enable a future-forward and healthy plantscape ecosystem" },
  ];
  return (
    <section data-testid="section-welcome" className="relative leaf-divider">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="pill-tag bg-[#C0AD21]/15 text-[#8a7a10]">Welcome</span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">
            Welcome to <span className="text-[#21C078]">Bloomers!</span>
          </h2>
          <p className="mt-5 text-lg text-[#1C1C1C]/75 leading-relaxed max-w-xl">
            Affordable & professional help… for you and your garden.
          </p>
          <ul className="mt-8 space-y-4">
            {bullets.map((b, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "0px 0px 200px 0px" }}
                transition={{ delay: i * 0.08 }}
                className="flex items-start gap-3"
              >
                <span className="w-9 h-9 grid place-items-center rounded-full bg-[#21C078]/10 text-[#21C078] shrink-0">
                  <b.icon className="w-5 h-5" />
                </span>
                <span className="text-[#1C1C1C] font-semibold">{b.text}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="relative">
          <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#21C0AD]/20 blur-2xl" />
          <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-[#21C078]/25 blur-2xl" />
          <img
            src="https://images.unsplash.com/photo-1779565145536-ccdf57517151"
            alt="Beautiful garden path"
            className="relative rounded-3xl object-cover w-full h-[480px] shadow-xl"
          />
          <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded-2xl p-4 shadow-lg">
            <div className="text-xs uppercase tracking-widest text-[#219DC0] font-black">Local knowledge</div>
            <div className="text-sm text-[#1C1C1C]/80">Serving the Greater Lake Norman region</div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -- Core Services ---------------------------------------------------------
function CoreServices() {
  const steps = [
    { icon: ClipboardList, title: "Onsite Client Consultation", desc: "We meet you in your yard, listen to your vision and take stock of the growing conditions unique to your property." },
    { icon: Palette, title: "Custom Vision Design & Estimate", desc: "If desired, Bloomers can draft a design reflecting your landscaping goals. We then develop a competitive estimate for relevant services and installation." },
    { icon: Truck, title: "Hand-select Plants & Install", desc: "We hand-pick every plant, coordinate our trusted install partners, and bring your garden to life. We remain onsite during installation to ensure successful completion." },
  ];
  return (
    <section data-testid="section-core-services" className="relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
          <div>
            <span className="pill-tag bg-[#21C0AD]/15 text-[#0f8577]">Core Services</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              Yard & Garden Design and Installation
            </h2>
          </div>
          <p className="text-[#1C1C1C]/70 text-lg max-w-xl lg:justify-self-end">
            We specialize in yard and garden design. We start with our clients' vision
            and end with a beautifully installed plantscape that delivers!
          </p>
        </div>

        <div className="mb-8 flex items-center gap-3">
          <span className="h-px w-16 bg-[#1C1C1C]/20" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#1C1C1C]/60">Our 3-step process · starts with listening</span>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px 200px 0px" }}
              transition={{ delay: i * 0.1 }}
              className="card p-8 relative overflow-hidden"
              data-testid={`service-step-${i + 1}`}
            >
              <div className="absolute top-4 right-6 text-3xl font-black text-[#21C078]/25 leading-none select-none uppercase tracking-tight">
                Step {i + 1}
              </div>
              <div className="w-14 h-14 grid place-items-center rounded-2xl bg-[#21C078]/10 text-[#21C078] mb-6">
                <s.icon className="w-7 h-7" />
              </div>
              <h3 className="mt-1 text-xl font-black">{s.title}</h3>
              <p className="mt-3 text-[#1C1C1C]/70 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -- Why Bloomers ---------------------------------------------------------
function WhyBloomers() {
  const items = [
    {
      key: "homeowners",
      icon: HandHeart,
      color: "#21C078",
      bg: "bg-[#21C078]/10",
      title: "Beautify + Build Home Equity",
      body:
        "Our clients are 'invested' homeowners who love surrounding themselves with a beautiful outdoor setting. Our design concepts harness that outdoor space — whether adding family play space, building privacy through hedging, attracting pollinators, or developing planted woodlands. We educate along the way; gardening success is the end goal.",
    },
    {
      key: "affordable",
      icon: Handshake,
      color: "#C0AD21",
      bg: "bg-[#C0AD21]/15",
      title: "Affordable & Professional",
      body:
        "From nationally-owned landscape companies to neighborhood lawn crews, homeowners have many options. Bloomers is neither — we are a local woman-owned plantscape design and installation service focused exclusively on proud, value-driven homeowners investing in their property's outdoor environment.",
    },
    {
      key: "lkn",
      icon: MapPin,
      color: "#219DC0",
      bg: "bg-[#219DC0]/10",
      title: "Local Knowledge — LKN",
      body:
        "We've spent 15+ years getting to know the Lake Norman area — its growing conditions, soil and seasonal nuances, in addition to the pests and plant diseases that can impact an otherwise healthy plantscape. Whether you're a local or a recent LKN transplant, our advice is grounded here.",
    },
  ];
  return (
    <section data-testid="section-why-bloomers" className="bg-[#F0EBE1]/60 border-y border-[#21C078]/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="max-w-2xl">
          <span className="pill-tag bg-[#21C078]/10 text-[#21C078]">Why Bloomers?</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
            Three reasons our clients keep growing with us.
          </h2>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px 200px 0px" }}
              transition={{ delay: i * 0.08 }}
              className="card p-8"
              data-testid={`why-card-${it.key}`}
            >
              <div className={`w-14 h-14 grid place-items-center rounded-2xl ${it.bg} mb-6`} style={{ color: it.color }}>
                <it.icon className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-black" style={{ color: it.color }}>{it.title}</h3>
              <p className="mt-3 text-[#1C1C1C]/75 leading-relaxed">{it.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -- Team --------------------------------------------------------------------
const TEAM_CARD_BG = {
  linda: "#c1a37f",
  andy: "#647047",
  ruby: "#628372",
  partners: "#F0EBE1",
};

function TeamCard({ m, index }) {
  const [open, setOpen] = useState(false);
  const bio = m.bio || "";
  const previewLen = 110;
  const needsToggle = bio.length > previewLen;
  const preview = needsToggle ? bio.slice(0, previewLen).trimEnd() + "…" : bio;
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px 200px 0px" }}
      transition={{ delay: index * 0.08 }}
      className="card overflow-hidden flex flex-col"
      data-testid={`team-card-${m.slug}`}
    >
      <div className="aspect-square overflow-hidden bg-[#F0EBE1]">
        {m.photo_url ? (
          <img
            src={absUrl(m.photo_url)}
            alt={m.name}
            width="600"
            height="600"
            className="w-full h-full object-cover"
            style={m.slug === "partners" ? { objectPosition: "calc(50% - 20px) 50%" } : { objectPosition: "center" }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#1C1C1C]/30">
            <TreePine className="w-16 h-16" />
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="text-xs uppercase font-bold tracking-widest text-[#21C078]">{m.title}</div>
        <h3 className="mt-1 text-xl font-black">{m.name}</h3>
        <p
          className="mt-3 text-sm text-[#1C1C1C]/70 leading-relaxed"
          data-testid={`team-bio-${m.slug}`}
        >
          {open || !needsToggle ? bio : preview}
        </p>
        {needsToggle && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-3 self-start inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-[#21C078] hover:text-[#1a9d61] transition-colors"
            data-testid={`team-toggle-${m.slug}`}
            aria-expanded={open}
          >
            {open ? (
              <>
                <Minus className="w-3.5 h-3.5" /> Show less
              </>
            ) : (
              <>
                <Plus className="w-3.5 h-3.5" /> Show more
              </>
            )}
          </button>
        )}
      </div>
    </motion.article>
  );
}

function Team() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, []);
  return (
    <section data-testid="section-team" className="relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
          <div>
            <span className="pill-tag bg-[#219DC0]/10 text-[#219DC0]">The Team</span>
            <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
              The Bloomers <span className="text-[#21C078]">crew</span>
            </h2>
          </div>
          <p className="text-[#1C1C1C]/70 text-lg max-w-xl lg:justify-self-end">
            Meet the people (and a very trusty truck) behind every project we design and install.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-start">
          {team.map((m, i) => (
            <TeamCard key={m.slug} m={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// -- Our Promise ----------------------------------------------------------
function OurPromise() {
  const items = [
    { title: "Experienced & Creative Designers", body: "Our knowledge of plants that thrive in the greater Lake Norman region allows us to design plantscapes that develop naturally and beautifully over time." },
    { title: "We Care About the Details", body: "Bloomers pays special attention to the unique details of every project to ensure the complete satisfaction of each client." },
    { title: "Client Satisfaction is Priority #1", body: "From the initial consultation until installation is finished, we pledge that your project is completed according to your preferences and delivers on your vision." },
  ];
  return (
    <section data-testid="section-promise" className="bg-[#21C078] text-white relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-32 -right-16 w-[520px] h-[520px] rounded-full bg-[#C0AD21]/25 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="max-w-3xl">
          <span className="pill-tag bg-white/15 text-white">Our Promise</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
            Personal, Practical, Affordable.
          </h2>
          <p className="mt-4 text-lg text-white/85 max-w-2xl leading-relaxed">
            Our ultimate goal — that you're completely satisfied with the <span className="whitespace-nowrap">end result</span>.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px 200px 0px" }}
              transition={{ delay: i * 0.08 }}
              className="rounded-3xl p-8 bg-white text-[#1C1C1C] shadow-xl ring-1 ring-white/40 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              data-testid={`promise-${i + 1}`}
            >
              <div className="w-12 h-12 grid place-items-center rounded-2xl bg-[#21C078]/15 text-[#21C078] mb-5">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-black text-[#1C1C1C]">{it.title}</h3>
              <p className="mt-3 text-[#1C1C1C]/70 leading-relaxed">{it.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14">
          <Link to="/contact" data-testid="promise-cta" className="inline-flex items-center gap-2 bg-white text-[#21C078] font-black px-8 py-3 rounded-full hover:-translate-y-0.5 transition-all shadow-lg">
            Start your project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${origin}/#organization`,
    name: "Bloomers — Inspired Plantscapes",
    image: `${origin}/bloomers-hero-logo.png`,
    logo: {
      "@type": "ImageObject",
      url: `${origin}/favicon.png`,
      width: 256,
      height: 256,
    },
    url: `${origin}/`,
    telephone: "+1-704-880-8969",
    email: "Linda@BloomersLKN.com",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      addressRegion: "NC",
      addressCountry: "US",
      addressLocality: "Denver",
    },
    areaServed: [
      { "@type": "Place", name: "Denver, NC" },
      { "@type": "Place", name: "Mooresville, NC" },
      { "@type": "Place", name: "Cornelius, NC" },
      { "@type": "Place", name: "Lake Norman, NC" },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "70",
    },
    sameAs: [
      "https://www.instagram.com/bloomers_lkn",
      "https://share.google/kTAlMfqKySXufXkFG",
    ],
  };
  return (
    <div data-testid="home-page">
      <Seo
        title="Local Woman-Owned Landscape Design in Lake Norman, NC"
        description="Bloomers is a local woman-owned yard and garden design & installation studio serving the greater Lake Norman region. 5.0★ on Google — 70+ reviews."
        path="/"
        jsonLd={localBusiness}
      />
      <HeroAnimation />
      <Welcome />
      <GoogleReviews />
      <CoreServices />
      <WhyBloomers />
      <Team />
      <OurPromise />
    </div>
  );
}
